import Divider from '@/components/shared/Divider/Divider';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AffiliateReportRow } from '@/@types/AffiliateReport';

interface Props {
  affiliate: AffiliateReportRow | null;
  isOpen: boolean;
  onClose: () => void;
}

function MetricRow({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  tooltip?: string;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0">
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-400">{label}</span>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-3.5 h-3.5 text-zinc-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px]">
                <p className="text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <span className="text-sm font-medium text-white">{value}</span>
    </div>
  );
}

export default function AffiliateDetailsSheet({
  affiliate,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation(['reports', 'affiliates']);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-[504px] p-0 overflow-y-auto">
        <section className="flex flex-col h-full">
          <SheetHeader className="p-6 border-b border-zinc-800">
            <SheetTitle className="flex flex-col gap-1 items-start">
              <span>{t('reports:affiliateReport.details.title')}</span>
              {affiliate ? (
                <span className="text-sm font-normal text-zinc-400">
                  {affiliate.name}
                </span>
              ) : (
                <Skeleton className="h-4 w-40 bg-zinc-800" />
              )}
            </SheetTitle>
          </SheetHeader>

          {affiliate && (
            <section className="p-6 space-y-4">
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">{t('affiliates:email')}</span>
                  <span>{affiliate.email}</span>
                </div>
              </div>

              <Divider />

              <div>
                <p className="text-sm font-semibold mb-2">
                  {t('reports:affiliateReport.details.metrics')}
                </p>
                <MetricRow
                  label={t('affiliates:gross_revenue')}
                  value={affiliate.metrics.revenue}
                  tooltip={t('affiliates:gross_revenue_description')}
                />
                <MetricRow
                  label={t('affiliates:total_commission')}
                  value={affiliate.metrics.commissionPaid}
                  tooltip={t('affiliates:total_commission_description')}
                />
                <MetricRow
                  label={t('reports:average_ticket')}
                  value={affiliate.metrics.aov}
                />
                <MetricRow
                  label={t('affiliates:producer_profit')}
                  value={affiliate.metrics.profit}
                  tooltip={t('affiliates:producer_profit_description')}
                />
                <MetricRow
                  label={t('reports:chargeback_rate')}
                  value={affiliate.metrics.chargebackRate}
                />
                <MetricRow
                  label={t('reports:refund_rate')}
                  value={affiliate.metrics.refundRate}
                />
              </div>
            </section>
          )}
        </section>
      </SheetContent>
    </Sheet>
  );
}
